import { useNavigate } from 'react-router-dom'
import type { KeyboardEvent, MouseEvent } from 'react'
import { Avatar, Badge, TableCell, TableRow } from '@ludus/ui'
import { managerDisplayName, type Manager } from '@ludus/core'
import type { LeadView, FunnelChannel } from '../../types.ts'
import { ageDays } from '../../utils/aging.ts'
import { FunnelPill } from '@/shared/components/FunnelPill.tsx'
import { TagChip } from '@/shared/components/TagChip.tsx'
import { useTags } from '@/shared/hooks/useTags.ts'

interface LeadRowProps {
  lead: LeadView
  manager: Manager | null
  funnel: FunnelChannel | null
  managerColor: string | null
  managerInitials: string | null
  now: Date
  /** Per-row stagger delay (ms) for the entrance cascade. */
  riseDelayMs?: number
}

const eur = new Intl.NumberFormat('en-EU', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function LeadRow({
  lead,
  manager,
  funnel,
  managerColor,
  managerInitials,
  now,
  riseDelayMs = 0,
}: LeadRowProps) {
  const { tagsMap } = useTags()
  const age = ageDays(lead.createdAt, now)
  const stuck = age > 14
  const navigate = useNavigate()
  const href = `/pipelines/${lead.pipelineId}/leads/${lead.id}`

  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    // Skip when the click landed on an interactive descendant (sort header inside
    // a cell, future inline-edit buttons, etc) so row-link doesn't hijack them.
    const target = event.target as HTMLElement
    if (target.closest('a, button, input, select, textarea')) return
    navigate(href)
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      navigate(href)
    }
  }

  return (
    <TableRow
      className="lead-row-rise cursor-pointer text-sm"
      style={{ animationDelay: `${riseDelayMs}ms` }}
      role="link"
      tabIndex={0}
      aria-label={`Open ${lead.contactName}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{lead.contactName}</span>
          <span className="text-[11px] text-muted">{lead.company}</span>
        </div>
      </TableCell>
      <TableCell>
        {manager ? (
          <div className="flex items-center gap-2">
            <Avatar
              size="xs"
              initials={
                managerInitials ??
                `${manager.firstName[0] ?? ''}${manager.lastName[0] ?? ''}`.toUpperCase()
              }
              color={managerColor ?? undefined}
              seed={manager.id}
              aria-label={managerDisplayName(manager)}
            />
            <span className="truncate text-foreground">{managerDisplayName(manager)}</span>
          </div>
        ) : (
          <span className="text-muted">—</span>
        )}
      </TableCell>
      <TableCell>
        {funnel ? (
          <FunnelPill abbr={funnel.abbr} label={funnel.label} />
        ) : (
          <span className="text-muted">—</span>
        )}
      </TableCell>
      <TableCell className="font-mono text-base font-semibold tabular-nums tracking-tight text-foreground">
        {eur.format(lead.value)}
      </TableCell>
      <TableCell>
        <span
          className={
            stuck
              ? 'font-mono tabular-nums text-toast-warning-text'
              : 'font-mono tabular-nums text-foreground'
          }
        >
          {age}d
        </span>
        {stuck && (
          <Badge tone="warning" size="sm" className="ml-2">
            stuck
          </Badge>
        )}
      </TableCell>
      <TableCell>
        {lead.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {lead.tags.map((tagId) => {
              const tag = tagsMap.get(tagId)
              return <TagChip key={tagId} label={tag?.name ?? tagId} color={tag?.color} />
            })}
          </div>
        ) : (
          <span className="text-muted">—</span>
        )}
      </TableCell>
      <TableCell>
        {lead.nextAction ? (
          <span className="text-foreground">{lead.nextAction}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}
